import { FC, useCallback, useMemo, useState } from "react";

import { Box } from "@mui/material";
import {
  CommunicationPreferenceFormType,
  FormType,
  isAddressMandatory,
  Locales,
  NOTIFICATION_PHONE_NUMBER_BODY,
  QuestionType,
} from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";

import { AddQuestionModal } from "./AddQuestionModal/AddQuestionModal";
import { CreateQuestionModal } from "./AddQuestionModal/CreateQuestionModal";
import { DonationLinkedQuestionsListV4 } from "./DonationLinkedQuestionsListV4";
import {
  buildPlaceholderDonorQuestions,
  mapApiDataToRows,
  mapQuestionsToPreviewFormat,
  restoreNotificationPhoneQuestion,
  toV3Input,
} from "./helpers/donationStepQuestionsV4Helpers";
import { buildExistingQuestionsAddedEvent } from "./helpers/buildExistingQuestionsAddedEvent";
import { useCustomQuestionsTracking } from "./helpers/useCustomQuestionsTracking";
import { createScopedQuestionDragHandler } from "./helpers/useScopedQuestionDragReorder";
import { QuestionCard } from "./QuestionCard/QuestionCard";
import {
  AddQuestionSelection,
  CreateQuestionEntryPoint,
  CreateQuestionFormData,
  CustomPropertyOption,
  DonationFormQuestionRowV4,
  DonationModalState,
  DonationStepQuestionsV4FormData,
  QuestionScope,
} from "./types";

import { CommunicationPreferencesCard } from "./CommunicationPreferences/CommunicationPreferencesCard";
import { EditCommunicationPreferencesModal } from "./CommunicationPreferences/EditCommunicationPreferencesModal";
import { useCommunicationPreferencesPreset } from "./CommunicationPreferences/helpers/useCommunicationPreferencesPreset";
import { useCommunicationPreferencesFlag } from "./hooks/useCommunicationPreferencesFlag";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc, AppRouterType } from "@/common/helpers/trpc";
import { useCommunicationChannelUsage } from "@/common/hooks/useCommunicationChannelUsage";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useAnimatePreviewOnCommunicationPreferences } from "@/features/LiveFormEditor/AnimatePreview/useAnimatePreviewOnCommunicationPreferences";
import { useScrollPreviewWithArrow } from "@/features/LiveFormEditor/AnimatePreview/useScrollPreviewWithArrow";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import { EditFundDesignationModal } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/EditFundDesignationModal";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";
import { Success } from "@/icons/outlined";

type FormFundOutput = inferRouterOutputs<AppRouterType>["getFormFunds"]["funds"][number];

const FundDesignationCard: FC<{
  funds?: FormFundOutput[];
  onEdit: () => void;
  onDelete: () => void;
  showDisabledReorderHandle: boolean;
}> = ({ funds, onEdit, onDelete, showDisabledReorderHandle }) => {
  const { t } = useTranslate();

  if (!funds || funds.length <= 1) {
    return null;
  }

  return (
    <QuestionCard
      icon={<Success />}
      label={t("dashboard", "funds.designation.label")}
      subtitle={funds.map((fund) => fund.name).join(t("dashboard", "funds.designation.fundSeparator"))}
      onEdit={onEdit}
      onDelete={onDelete}
      showDisabledReorderHandle={showDisabledReorderHandle}
    />
  );
};

export const DonationLiveEditorStepQuestionsV4: FC = () => {
  const { apiFormObject: apiDonationForm, setPreviewStepKey } = useLiveFormEditorContext<"donation-form">();
  const { scrollAndUpdateArrow } = useScrollPreviewWithArrow();
  const { organization } = useCurrentUserContext();
  const { t } = useTranslate();
  const utils = trpc.useUtils();

  const [modalState, setModalState] = useState<DonationModalState>({ type: "closed" });
  const [isSaving, setIsSaving] = useState(false);
  const [isFundDesignationModalOpen, setIsFundDesignationModalOpen] = useState(false);
  const [isCommunicationPreferencesModalOpen, setIsCommunicationPreferencesModalOpen] = useState(false);

  const { isActive: isCommunicationPreferencesFlagActive } = useCommunicationPreferencesFlag();
  // Peer-to-peer team and individual pages inherit the campaign form's preferences, so they have nothing to edit.
  const isCommunicationPreferencesEnabled =
    isCommunicationPreferencesFlagActive &&
    !(Boolean(apiDonationForm.campaignId) && !apiDonationForm.isPrimaryCampaign);

  const {
    focusCommunicationPreferences,
    focusAddedCommunicationPreferences,
    handleQuestionsFocus: handleCommunicationPreferencesFocus,
    hideArrow: hideCommunicationPreferencesArrow,
  } = useAnimatePreviewOnCommunicationPreferences();

  // The question is unique per form: once it is offered, the card is the only way to edit it.
  const {
    presets: communicationPreferencesPresets,
    addFromSelection: addCommunicationPreferences,
    removeFromForm: removeCommunicationPreferences,
    isOfferedOnForm: formOffersCommunicationPreferences,
  } = useCommunicationPreferencesPreset({
    formId: apiDonationForm.id,
    formType: CommunicationPreferenceFormType.DonationForm,
    isEnabled: isCommunicationPreferencesEnabled,
    onAdded: focusAddedCommunicationPreferences,
    onRemoved: hideCommunicationPreferencesArrow,
  });

  const logCustomQuestionsEvent = useCustomQuestionsTracking();

  const { data: questionsData, refetch: refetchQuestionsData } = trpc.getQuestionsStepData.useQuery({
    donationFormId: apiDonationForm.id,
  });

  const { data: customPropertiesData, isLoading: isLoadingProperties } = trpc.getOrganizationCustomProperties.useQuery({
    locale: apiDonationForm.locale,
  });

  const { data: fundsDataResult } = trpc.getFormFunds.useQuery({
    formId: apiDonationForm.id,
    formType: FormType.DonationForm,
    locale: apiDonationForm.locale,
  });

  const fundsData = fundsDataResult?.funds;

  const { mutateAsync: updateFormFunds } = trpc.updateFormFunds.useMutation();
  const updateQuestionsV3 = trpc.updateQuestionsV3.useMutation();

  const isAddressMandatoryValue = isAddressMandatory(organization, apiDonationForm.hasReceipt);

  const defaultValues: DonationStepQuestionsV4FormData = useMemo(
    () => ({
      questions: questionsData?.questions ? mapApiDataToRows(questionsData.questions) : [],
      askForAddress: isAddressMandatoryValue
        ? true
        : Boolean(questionsData?.askForAddress ?? apiDonationForm.askForAddress),
    }),
    [questionsData, isAddressMandatoryValue, apiDonationForm.askForAddress]
  );

  const form = useForm<DonationStepQuestionsV4FormData>({ defaultValues, values: defaultValues });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.donation.askForAddress = formData.askForAddress ?? false;

      const activeQuestionsForPreview = formData.questions.filter((q) => q.isActiveOnForm);

      if (!activeQuestionsForPreview.length) {
        liveDataToMutate.donation.questions = buildPlaceholderDonorQuestions(t, apiDonationForm.locale);
        liveDataToMutate.placeholders.isBuyerQuestions = true;
        return;
      }

      liveDataToMutate.donation.questions = mapQuestionsToPreviewFormat(formData.questions, apiDonationForm.locale);
      liveDataToMutate.placeholders.isBuyerQuestions = false;
    },
  });

  const watchedQuestions = form.watch("questions");
  const localQuestions = useMemo(() => watchedQuestions ?? [], [watchedQuestions]);
  const askForAddress = form.watch("askForAddress");
  const activeQuestions = localQuestions.filter((q) => q.isActiveOnForm);

  const questionNamesOnForm = useMemo(() => localQuestions.map((q) => q.body), [localQuestions]);

  const { hasSmsChannel: orgSendsSms } = useCommunicationChannelUsage();

  const existingCustomProperties: CustomPropertyOption[] = useMemo(() => {
    const apiProperties = customPropertiesData?.customProperties ?? [];
    const localPropertyIds = new Set(activeQuestions.map((q) => q.crmCustomPropertyId).filter(Boolean));

    return apiProperties.map((p) => ({
      ...p,
      scopesOnForm: localPropertyIds.has(p.id) ? (["buyer"] as QuestionScope[]) : [],
    }));
  }, [customPropertiesData, activeQuestions]);

  const notificationPhoneLabel = NOTIFICATION_PHONE_NUMBER_BODY[(apiDonationForm.locale as Locales) || Locales.EN];
  const hasActiveNotificationPhoneQuestion = activeQuestions.some(
    (q) => q.type === QuestionType.NotificationPhoneNumber
  );
  const showNotificationPhoneOption = orgSendsSms && !hasActiveNotificationPhoneQuestion;

  const saveQuestions = async (questions: DonationFormQuestionRowV4[], askForAddressValue: boolean) => {
    setIsSaving(true);
    try {
      await updateQuestionsV3.mutateAsync({
        donationFormId: apiDonationForm.id,
        questions: toV3Input(questions),
        askForAddress: askForAddressValue,
      });
      await refetchQuestionsData();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteQuestion = (localId: string) => {
    const question = localQuestions.find((q) => q.localId === localId);
    if (!question) {
      return;
    }

    if (question.isNew) {
      const updatedQuestions = localQuestions.filter((q) => q.localId !== localId);
      form.setValue("questions", updatedQuestions, { shouldDirty: true });
      setPreviewStepKey("payment");
      return;
    }

    const updatedQuestions = localQuestions.map((q) => (q.localId === localId ? { ...q, isActiveOnForm: false } : q));
    form.setValue("questions", updatedQuestions, { shouldDirty: true });
    setPreviewStepKey("payment");
  };

  const openCreateModal = (
    _scope: "buyer" | "participant",
    _selectedIds: string[],
    prefillBody?: string,
    entryPoint?: CreateQuestionEntryPoint
  ) => {
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionCreationStarted);
    setModalState({ type: "create-new", prefillBody, entryPoint });
  };

  const handleOpenAddModal = () => {
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionsModalOpened);
    setModalState({ type: "add-question" });
  };

  const handleQuestionSearch = useCallback(
    (audience: QuestionScope, hasResults: boolean) => {
      logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionSearched, {
        audience: audience === "buyer" ? "buyer" : "guest",
        has_results: hasResults,
      });
    },
    [logCustomQuestionsEvent]
  );

  const handleCreateQuestion = (data: CreateQuestionFormData) => {
    const newQuestion: DonationFormQuestionRowV4 = {
      localId: v4(),
      questionId: null,
      crmCustomPropertyId: data.matchedCustomProperty?.id ?? null,
      body: data.body,
      type: data.type,
      required: data.required,
      scope: "buyer",
      choices: data.choices.map((c, idx) => ({ id: v4(), body: c.body, sortIndex: idx })),
      sortIndex: 0,
      isActiveOnForm: true,
      isNew: true,
      isDirty: true,
    };

    const updatedQuestions = [...localQuestions, newQuestion];
    form.setValue("questions", updatedQuestions, { shouldDirty: true });
    if (data.matchedCustomProperty) {
      logCustomQuestionsEvent(AmplitudeEvents.ExistingCustomQuestionAdded, {
        questions_added_count: 1,
        source: "creation-name-match",
      });
    } else {
      logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionCreated, {
        question_audience: "buyer",
        response_format: data.type,
        is_required: data.required,
        ...(modalState.type === "create-new" && modalState.entryPoint && { entry_point: modalState.entryPoint }),
      });
    }
    setModalState({ type: "closed" });
    setPreviewStepKey("payment");
  };

  const handleAddSelected = (selection: AddQuestionSelection) => {
    if (selection.addFullAddress) {
      form.setValue("askForAddress", true, { shouldDirty: true });
    }

    void addCommunicationPreferences(selection);

    const buildRow = (partial: Partial<DonationFormQuestionRowV4>): DonationFormQuestionRowV4 => ({
      localId: v4(),
      questionId: null,
      crmCustomPropertyId: null,
      body: "",
      type: QuestionType.Simple,
      required: false,
      scope: "buyer",
      choices: [],
      sortIndex: 0,
      isActiveOnForm: true,
      isNew: true,
      isDirty: true,
      ...partial,
    });

    const crmQuestions: DonationFormQuestionRowV4[] = selection.buyerPropertyIds
      .map((id) => existingCustomProperties.find((p) => p.id === id))
      .filter((property): property is CustomPropertyOption => Boolean(property))
      .map((property) =>
        buildRow({
          crmCustomPropertyId: property.id,
          body: property.body,
          type: property.type,
          choices: property.choices.map((c, idx) => ({ id: v4(), body: c.body, sortIndex: idx })),
        })
      );

    const phoneNumberQuestions: DonationFormQuestionRowV4[] = selection.addPhoneNumber
      ? [
          buildRow({
            body: t("dashboard", "donationForm.questionBodyDefaultValue.PhoneNumber"),
            type: QuestionType.PhoneNumber,
          }),
        ]
      : [];

    const notificationPhoneQuestions: DonationFormQuestionRowV4[] = selection.addNotificationPhoneNumber
      ? [restoreNotificationPhoneQuestion(localQuestions, buildRow, notificationPhoneLabel)]
      : [];

    const addedQuestions = [...phoneNumberQuestions, ...notificationPhoneQuestions, ...crmQuestions];
    if (addedQuestions.length > 0) {
      form.setValue("questions", [...localQuestions, ...addedQuestions], { shouldDirty: true });
    }
    const existingQuestionsAddedEvent = buildExistingQuestionsAddedEvent({
      reusedQuestionCount: addedQuestions.length,
      addFullAddress: selection.addFullAddress,
    });
    if (existingQuestionsAddedEvent) {
      logCustomQuestionsEvent(AmplitudeEvents.ExistingCustomQuestionAdded, existingQuestionsAddedEvent);
    }
    setModalState({ type: "closed" });
    setPreviewStepKey("payment");
  };

  const handleEditQuestion = (question: DonationFormQuestionRowV4) => {
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionEditionStarted, {
      question_audience: question.scope,
      response_format: question.type,
    });
    setModalState({ type: "edit", question });
  };

  const handleUpdateQuestion = (data: CreateQuestionFormData) => {
    if (modalState.type !== "edit") {
      return;
    }

    const updatedQuestions = localQuestions.map((q) =>
      q.localId === modalState.question.localId
        ? {
            ...q,
            body: data.body,
            type: data.type,
            required: data.required,
            choices: data.choices.map((c, idx) => ({ id: v4(), body: c.body, sortIndex: idx })),
            isDirty: true,
          }
        : q
    );

    form.setValue("questions", updatedQuestions, { shouldDirty: true });
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionEdited, {
      question_audience: "buyer",
      question_is_buyer_guest_changed: false,
      response_format: data.type,
      is_required: data.required,
    });
    setModalState({ type: "closed" });
    setPreviewStepKey("payment");
  };

  const handleCloseModal = () => {
    setModalState({ type: "closed" });
  };

  const handleDragEnd = createScopedQuestionDragHandler(localQuestions, "buyer", form.setValue);

  const hasActivePhoneQuestion = activeQuestions.some((q) => q.type === QuestionType.PhoneNumber);
  const hasSelectablePhoneProperty = existingCustomProperties.some(
    (property) =>
      property.type === QuestionType.PhoneNumber &&
      property.scope === "buyer" &&
      !property.scopesOnForm.includes("buyer")
  );

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<DonationStepQuestionsV4FormData>({
    type: "donation-form",
    form,
    saveData: async (formData) => {
      try {
        await saveQuestions(formData.questions, formData.askForAddress);
        return { data: apiDonationForm, error: null };
      } catch {
        return { data: null, error: { code: "save_questions_failed", message: "Failed to save questions" } };
      }
    },
  });

  const editingQuestion = modalState.type === "edit" ? modalState.question : null;
  const createModalPrefillBody = modalState.type === "create-new" ? modalState.prefillBody : undefined;

  const scrollToFundDesignation = useCallback(() => {
    setPreviewStepKey("selection");
    setTimeout(() => {
      scrollAndUpdateArrow({ fundDesignationInFocus: true });
    }, 500);
  }, [scrollAndUpdateArrow, setPreviewStepKey]);

  const handleEditFundDesignation = useCallback(() => {
    setIsFundDesignationModalOpen(true);
    scrollToFundDesignation();
  }, [scrollToFundDesignation]);

  const handleCloseFundDesignationModal = useCallback(() => {
    setIsFundDesignationModalOpen(false);
  }, []);

  const handleFundDesignationSaved = useCallback(() => {
    setPreviewStepKey("selection");
    setIsFundDesignationModalOpen(false);
  }, [setPreviewStepKey]);

  const handleDeleteFundDesignation = useCallback(async () => {
    const { error } = await updateFormFunds({
      formId: apiDonationForm.id,
      formType: FormType.DonationForm,
      fundIds: [],
      isRequired: false,
      locale: apiDonationForm.locale,
    });
    if (error) {
      enqueueSnackbar(t("dashboard", "funds.editDesignation.removeError"), { vibe: "danger" });
      return;
    }
    await utils.getFormFunds.invalidate({
      formId: apiDonationForm.id,
      formType: FormType.DonationForm,
      locale: apiDonationForm.locale,
    });
  }, [updateFormFunds, apiDonationForm.id, apiDonationForm.locale, utils, t]);

  return (
    <FormStepContainer
      title={t("dashboard", "stepQuestionsV4.title.donation")}
      description={t("dashboard", "liveFormEditor.stepQuestions.description.buyer")}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        nextButtonLabel:
          activeQuestions.length === 0 && !fundsData?.length && !formOffersCommunicationPreferences ? "skip" : "next",
      }}
      form={form}
      animatePreview={false}
    >
      <Box onFocus={handleCommunicationPreferencesFocus}>
        <DonationLinkedQuestionsListV4
          questions={activeQuestions}
          onReorder={handleDragEnd}
          onEdit={handleEditQuestion}
          onDelete={handleDeleteQuestion}
          onAddQuestion={handleOpenAddModal}
          askForAddress={askForAddress}
          onDeleteAddress={() => form.setValue("askForAddress", false, { shouldDirty: true })}
          additionalOptions={
            <FundDesignationCard
              funds={fundsData}
              onEdit={handleEditFundDesignation}
              onDelete={handleDeleteFundDesignation}
              showDisabledReorderHandle={activeQuestions.length > 1}
            />
          }
          trailingOptions={
            isCommunicationPreferencesEnabled && (
              <CommunicationPreferencesCard
                formId={apiDonationForm.id}
                formType={CommunicationPreferenceFormType.DonationForm}
                onEdit={() => {
                  setIsCommunicationPreferencesModalOpen(true);
                  focusCommunicationPreferences();
                }}
                onDelete={removeCommunicationPreferences}
                showDisabledReorderHandle={activeQuestions.length > 1}
              />
            )
          }
        />
      </Box>

      <AddQuestionModal
        isOpen={modalState.type === "add-question"}
        initialScope="buyer"
        onClose={handleCloseModal}
        existingCustomProperties={existingCustomProperties}
        isLoadingProperties={isLoadingProperties}
        onAddSelected={handleAddSelected}
        onCreateFromScratch={openCreateModal}
        onSearch={handleQuestionSearch}
        showFullAddressOption={!askForAddress}
        showPhoneNumberOption={!hasActivePhoneQuestion && !hasSelectablePhoneProperty}
        showNotificationPhoneOption={showNotificationPhoneOption}
        notificationPhoneLabel={notificationPhoneLabel}
        buyerPresets={communicationPreferencesPresets}
        guestPresets={[]}
        showGuestTab={false}
        titleOverride={t("dashboard", "customQuestion.addModalTitle")}
        transitionDuration={modalState.type === "create-new" ? 0 : undefined}
      />

      <CreateQuestionModal
        key={editingQuestion?.localId ?? `new-${createModalPrefillBody ?? ""}`}
        isOpen={modalState.type === "create-new" || modalState.type === "edit"}
        transitionDuration={modalState.type === "create-new" ? 0 : undefined}
        onClose={handleCloseModal}
        onSubmit={editingQuestion ? handleUpdateQuestion : handleCreateQuestion}
        isLoading={isSaving}
        isEditing={!!editingQuestion}
        disableNonEditableFields={!(editingQuestion?.isNew ?? true)}
        questionNamesOnForm={questionNamesOnForm}
        matchableProperties={existingCustomProperties}
        showFundDesignation={true}
        showCommunicationPreferences={isCommunicationPreferencesEnabled && !formOffersCommunicationPreferences}
        communicationPreferenceFormType={CommunicationPreferenceFormType.DonationForm}
        onCommunicationPreferencesSaved={focusAddedCommunicationPreferences}
        formKind="donation-form"
        formId={apiDonationForm.id}
        formType={FormType.DonationForm}
        locale={apiDonationForm.locale}
        defaultValues={
          editingQuestion
            ? {
                body: editingQuestion.body,
                type: editingQuestion.type,
                required: editingQuestion.required,
                choices: editingQuestion.choices.map((c) => ({ body: c.body })),
              }
            : { body: createModalPrefillBody }
        }
      />

      <EditFundDesignationModal
        isOpen={isFundDesignationModalOpen}
        onClose={handleCloseFundDesignationModal}
        donationFormId={apiDonationForm.id}
        locale={apiDonationForm.locale}
        onSaved={handleFundDesignationSaved}
        onFundAdded={scrollToFundDesignation}
      />

      {isCommunicationPreferencesModalOpen && (
        <EditCommunicationPreferencesModal
          isOpen
          formId={apiDonationForm.id}
          formType={CommunicationPreferenceFormType.DonationForm}
          formLocale={apiDonationForm.locale}
          onClose={() => setIsCommunicationPreferencesModalOpen(false)}
        />
      )}
    </FormStepContainer>
  );
};
